// MUST be first: several modules read process.env at module scope, and
// imports are evaluated in order. See load-env.ts.
import './load-env';
import { Logger, ValidationPipe } from '@nestjs/common';
import { NestFactory } from '@nestjs/core';
import { NestExpressApplication } from '@nestjs/platform-express';
import { DocumentBuilder, SwaggerModule } from '@nestjs/swagger';
import * as path from 'node:path';
import { AppModule } from './app/app.module';
import { assertStorageEnvironmentConsistent } from './app/media/platform-storage.constants';
import { assertBackupEnvironmentConsistent } from './app/database/backup-storage.constants';

const SWAGGER_CSS = `
  /* ── Layout ── */
  body { font-family: 'Inter', 'Segoe UI', system-ui, sans-serif; }
  .swagger-ui .topbar { display: none !important; }
  .swagger-ui { max-width: 1200px; margin: 0 auto; }

  /* ── Info header ── */
  .swagger-ui .info { padding: 32px 0 24px; border-bottom: 2px solid #e5e7eb; margin-bottom: 28px; }
  .swagger-ui .info .title {
    font-size: 1.9rem !important; font-weight: 800; color: #0f172a;
    letter-spacing: -0.03em; display: flex; align-items: center; gap: 12px;
  }
  .swagger-ui .info .title small.version-stamp {
    background: #6366f1; color: #fff; padding: 3px 12px; border-radius: 99px;
    font-size: 0.6rem; font-weight: 700; letter-spacing: 0.08em;
    text-transform: uppercase; vertical-align: middle;
  }
  .swagger-ui .info p { font-size: 0.9rem; color: #475569; line-height: 1.7; margin: 0; }
  .swagger-ui .info p strong { color: #0f172a; }
  .swagger-ui .info p code {
    background: #f1f5f9; color: #6366f1; padding: 1px 6px;
    border-radius: 4px; font-size: 0.85em; font-family: 'Fira Code', monospace;
  }
  .swagger-ui .info a { color: #6366f1; }

  /* ── Auth / scheme container ── */
  .swagger-ui .scheme-container {
    background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px;
    margin: 0 0 28px; padding: 18px 24px; box-shadow: none;
  }
  .swagger-ui .auth-wrapper { display: flex; align-items: center; gap: 12px; }
  .swagger-ui .auth-wrapper .btn.authorize {
    background: #6366f1 !important; border-color: #6366f1 !important;
    color: #fff !important; border-radius: 7px !important; font-weight: 700 !important;
    padding: 9px 22px !important; font-size: 0.85rem !important; cursor: pointer;
    transition: background 0.15s;
  }
  .swagger-ui .auth-wrapper .btn.authorize:hover { background: #4f46e5 !important; }
  .swagger-ui .auth-wrapper .btn.authorize svg { fill: #fff !important; }

  /* ── Filter bar ── */
  .swagger-ui .filter-container { padding: 0 0 20px; }
  .swagger-ui .filter-container .filter { display: flex; align-items: center; gap: 8px; }
  .swagger-ui .filter-container .filter input {
    border: 1px solid #d1d5db; border-radius: 7px; padding: 8px 14px;
    font-size: 0.875rem; width: 340px; outline: none; color: #1e293b;
    transition: border-color 0.15s, box-shadow 0.15s;
  }
  .swagger-ui .filter-container .filter input:focus {
    border-color: #6366f1; box-shadow: 0 0 0 3px rgba(99,102,241,0.15);
  }

  /* ── Tag section headers ── */
  .swagger-ui .opblock-tag-section h3.opblock-tag {
    font-size: 0.95rem !important; font-weight: 700 !important; color: #0f172a !important;
    background: #f8fafc !important; border: 1px solid #e2e8f0 !important;
    border-radius: 8px !important; padding: 13px 18px !important;
    margin-bottom: 8px !important; transition: background 0.15s;
  }
  .swagger-ui .opblock-tag-section h3.opblock-tag:hover { background: #f1f5f9 !important; }
  .swagger-ui .opblock-tag-section h3.opblock-tag small {
    color: #94a3b8 !important; font-weight: 400 !important; font-size: 0.8rem !important;
  }
  .swagger-ui .opblock-tag-section h3.opblock-tag a,
  .swagger-ui .opblock-tag-section h3.opblock-tag span {
    color: #0f172a !important;
  }

  /* ── Operation blocks ── */
  .swagger-ui .opblock {
    border-radius: 7px !important; box-shadow: 0 1px 3px rgba(0,0,0,0.05) !important;
    margin-bottom: 6px !important; overflow: hidden;
  }
  .swagger-ui .opblock.opblock-get    { border: 1px solid #bfdbfe !important; background: #f0f7ff !important; }
  .swagger-ui .opblock.opblock-post   { border: 1px solid #a7f3d0 !important; background: #f0fdf6 !important; }
  .swagger-ui .opblock.opblock-patch  { border: 1px solid #fed7aa !important; background: #fffbf5 !important; }
  .swagger-ui .opblock.opblock-delete { border: 1px solid #fca5a5 !important; background: #fff5f5 !important; }
  .swagger-ui .opblock.opblock-put    { border: 1px solid #c4b5fd !important; background: #faf5ff !important; }

  /* ── Method badges ── */
  .swagger-ui .opblock-summary-method {
    border-radius: 5px !important; font-weight: 800 !important;
    font-size: 0.68rem !important; min-width: 72px !important;
    text-align: center !important; letter-spacing: 0.04em !important;
  }
  .swagger-ui .opblock.opblock-get    .opblock-summary-method { background: #2563eb !important; }
  .swagger-ui .opblock.opblock-post   .opblock-summary-method { background: #059669 !important; }
  .swagger-ui .opblock.opblock-patch  .opblock-summary-method { background: #d97706 !important; }
  .swagger-ui .opblock.opblock-delete .opblock-summary-method { background: #dc2626 !important; }
  .swagger-ui .opblock.opblock-put    .opblock-summary-method { background: #7c3aed !important; }

  /* ── Summary text ── */
  .swagger-ui .opblock-summary-description {
    font-size: 0.875rem !important; color: #334155 !important; font-weight: 500 !important;
  }
  .swagger-ui .opblock-summary-path {
    font-size: 0.875rem !important; color: #64748b !important; font-weight: 400 !important;
    font-family: 'Fira Code', 'Cascadia Code', monospace !important;
  }
  .swagger-ui .opblock-summary-path__deprecated { text-decoration: line-through; }

  /* ── Lock icon (auth) ── */
  .swagger-ui .authorization__btn svg { fill: #6366f1 !important; }

  /* ── Buttons ── */
  .swagger-ui .btn.try-out__btn {
    background: #6366f1 !important; color: #fff !important;
    border: none !important; border-radius: 5px !important; font-weight: 600 !important;
    font-size: 0.8rem !important; padding: 6px 14px !important;
  }
  .swagger-ui .btn.try-out__btn:hover { background: #4f46e5 !important; }
  .swagger-ui .btn.execute {
    background: #0f172a !important; border-color: #0f172a !important;
    border-radius: 5px !important; font-weight: 700 !important;
    padding: 8px 20px !important;
  }
  .swagger-ui .btn.execute:hover { background: #1e293b !important; }
  .swagger-ui .btn.btn-clear {
    color: #6366f1 !important; border-color: #6366f1 !important;
    border-radius: 5px !important;
  }

  /* ── Response section ── */
  .swagger-ui .responses-inner h4,
  .swagger-ui .responses-inner h5 { font-size: 0.85rem !important; color: #475569; }
  .swagger-ui table.responses-table td { padding: 8px 12px; }
  .swagger-ui .response-col_status { font-weight: 700; font-size: 0.85rem; }
  .swagger-ui .response-col_description { color: #374151; font-size: 0.85rem; }
  .swagger-ui .response-col_links { color: #94a3b8; font-size: 0.8rem; }

  /* ── Response status pills ── */
  .swagger-ui .response-col_status span[class*="response-col"] { border-radius: 4px; padding: 2px 8px; }

  /* ── Code blocks ── */
  .swagger-ui .microlight, .swagger-ui pre.microlight {
    background: #1e293b !important; color: #e2e8f0 !important;
    border-radius: 6px !important; font-size: 0.8rem !important;
    padding: 14px !important; font-family: 'Fira Code', monospace !important;
  }

  /* ── Hide schemas section (interfaces don't produce useful schema docs) ── */
  .swagger-ui section.models { display: none !important; }

  /* ── Scrollbar ── */
  ::-webkit-scrollbar { width: 5px; height: 5px; }
  ::-webkit-scrollbar-track { background: #f8fafc; }
  ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
  ::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
`;

const SWAGGER_DESCRIPTION = `
Multi-store commerce backend for the **Shopeee** platform.
Supports a full shopping flow — browse, cart, checkout, orders — plus a customer dashboard and an admin portal.

**Quick start:**
1. Call \`POST /api/auth/register\` or \`POST /api/auth/login\`
2. Copy the \`accessToken\` from the response
3. Click **Authorize** above → paste the token into **BearerAuth**
4. Optionally set \`x-store-id\` to scope data to a specific store

**Token lifetime:** 1 hour. Use \`POST /api/auth/refresh\` to renew without re-logging in.
`.trim();

async function bootstrap() {
  // Before the app exists, so a mismatch cannot reach an upload handler.
  assertStorageEnvironmentConsistent();
  // Same check for the backup store. A mis-set prefix here is worse than for
  // media: the retention job deletes to stay inside quota, so a laptop pointed
  // at `production` could expire real backups.
  assertBackupEnvironmentConsistent();

  const app = await NestFactory.create<NestExpressApplication>(AppModule, { rawBody: true });

  // Uploaded product images (see LocalDiskImageStorageAdapter) live under
  // UPLOADS_DIR and are served as plain static files. Mounted under /api/
  // (not a bare /uploads) so every frontend's existing /api/* proxy (dev)
  // and reverse-proxy rule (prod, per plan.md's Caddy routing) reaches them
  // without needing a second route added anywhere.
  app.useStaticAssets(process.env.UPLOADS_DIR ?? path.join(process.cwd(), 'uploads'), {
    prefix: '/api/uploads',
  });

  // Apache proxies every request from 127.0.0.1 — without this, Express
  // ignores X-Forwarded-For and req.ip resolves to that same loopback
  // address for every visitor, so ThrottlerGuard's per-IP rate limit ends
  // up shared across the entire site's combined traffic instead of per
  // client.
  app.getHttpAdapter().getInstance().set('trust proxy', 'loopback');

  const globalPrefix = 'api';
  app.setGlobalPrefix(globalPrefix);

  app.useGlobalPipes(new ValidationPipe({ transform: true, whitelist: true }));

  const swaggerConfig = new DocumentBuilder()
    .setTitle('Shopeee Commerce API')
    .setDescription(SWAGGER_DESCRIPTION)
    .setVersion('1.0')
    .addBearerAuth(
      { type: 'http', scheme: 'bearer', bearerFormat: 'JWT', description: 'Paste your accessToken here (without the "Bearer " prefix)' },
      'BearerAuth'
    )
    .addApiKey(
      { type: 'apiKey', name: 'x-store-id', in: 'header', description: 'Store identifier. Omit to use the default store.' },
      'store-id'
    )
    /* ── Tag order defines sidebar order ── */
    .addTag('Auth', 'Register, login, token refresh and stateless logout')
    .addTag('Products', 'Browse, search and filter the product catalogue')
    .addTag('Categories', 'Product category listing')
    .addTag('Cart', 'Session cart — add, update and remove items')
    .addTag('Checkout', 'Checkout session lifecycle: create → update → complete')
    .addTag('Payments', 'Razorpay payment integration — create order, verify, webhook')
    .addTag('Orders', 'Customer order history and detail')
    .addTag('Account', 'User profile and password management')
    .addTag('Addresses', 'Saved shipping addresses for the current user')
    .addTag('Wishlist', 'Product save-for-later list')
    .addTag('Admin · Products', 'Product CRUD, inventory and soft-deactivation  🔒 admin')
    .addTag('Admin · Categories', 'Category management  🔒 admin')
    .addTag('Admin · Orders', 'Order listing and status updates  🔒 admin')
    .addTag('Admin · Users', 'User listing  🔒 admin')
    .addTag('Admin · Platform', 'Organisations, stores, staff, shipping, tax, marketing and audit  🔒 admin')
    .addTag('Storefront', 'Public store config, pages and navigation menus')
    .addTag('Health', 'API liveness and database connectivity check')
    .build();

  const swaggerDocument = SwaggerModule.createDocument(app, swaggerConfig);

  SwaggerModule.setup(`${globalPrefix}/docs`, app, swaggerDocument, {
    customSiteTitle: 'Shopeee API Docs',
    customCss: SWAGGER_CSS,
    swaggerOptions: {
      persistAuthorization: true,
      filter: true,
      displayRequestDuration: true,
      docExpansion: 'none',
      defaultModelsExpandDepth: -1,
      syntaxHighlight: { activate: true, theme: 'monokai' },
    },
  });

  const port = process.env.PORT || 3000;
  await app.listen(port);
  Logger.log(`API running on:  http://localhost:${port}/${globalPrefix}`);
  Logger.log(`Swagger UI:      http://localhost:${port}/${globalPrefix}/docs`);
}

bootstrap();
